import { Helmet } from "react-helmet-async";
import { ORGANIZATION_SCHEMA } from "@/lib/schemas";

type SchemaObject = Record<string, unknown>;
type SchemaJsonLd = SchemaObject | SchemaObject[];

export interface SEOProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  /**
   * Page-specific JSON-LD schemas. The sitewide Organization schema is always
   * injected automatically — pass additional schemas (Course, FAQPage,
   * LocalBusiness, Article, …) here.
   */
  schemaJsonLd?: SchemaJsonLd;
}

const SITE_NAME = "Taekwondo Mattei Roma";
const SITE_ORIGIN = "https://taekwondomattei.com";
const DEFAULT_OG_IMAGE = "/og-home.jpg";

const toAbsolute = (url: string) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
};

export const SEO = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noindex = false,
  schemaJsonLd,
}: SEOProps) => {
  const absImage = toAbsolute(ogImage);
  const absCanonical = toAbsolute(canonical);

  const extras = schemaJsonLd
    ? Array.isArray(schemaJsonLd)
      ? schemaJsonLd
      : [schemaJsonLd]
    : [];
  const allSchemas: SchemaObject[] = [ORGANIZATION_SCHEMA, ...extras];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={absCanonical} />

      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absImage} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={absCanonical} />
      <meta property="og:locale" content="it_IT" />
      <meta property="og:site_name" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />

      {allSchemas.map((schema, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
